import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trophy, Star, ClipboardList, Sparkles, Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import { preloadImagesForCapture } from '@/lib/preloadImagesForCapture';
import { RecapSquareCard } from '@/components/stats/RecapSquareCard';
import { formatGameDate } from '@/lib/formatGameDate';
import { runsWickets } from '@/lib/cricket';
import { StatLegend } from '@/components/stats/StatLegend';
import { shareHero, recapPalette } from '@/lib/shareTheme';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------//
// Basketball helper (kept in-file since it's only used when a basketball    //
// stats-game is linked to this match). All other sports render the          //
// sport-aware summary further down.                                          //
// ---------------------------------------------------------------------------//

function BasketballPlayerRow({ player, highlight }) {
  const fgPct = player.fga > 0 ? ((player.fgm / player.fga) * 100).toFixed(0) : '-';
  return (
    <tr className={`border-b border-slate-100 ${highlight ? 'bg-amber-50' : ''}`}>
      <td className="px-2 py-2 text-sm font-mono text-slate-500 text-center">{player.jersey}</td>
      <td className="px-2 py-2 text-sm font-semibold text-slate-800">
        {player.name}
        {highlight && <Star className="w-3 h-3 inline ml-1 text-amber-500" />}
      </td>
      <td className="px-2 py-2 text-sm text-center font-bold text-[#00247D]">{player.pts}</td>
      <td className="px-2 py-2 text-sm text-center">{player.reb}</td>
      <td className="px-2 py-2 text-sm text-center">{player.ast}</td>
      <td className="px-2 py-2 text-sm text-center">{player.stl}</td>
      <td className="px-2 py-2 text-sm text-center">{player.blk}</td>
      <td className="px-2 py-2 text-sm text-center text-red-500">{player.to}</td>
      <td className="px-2 py-2 text-sm text-center text-slate-500">{fgPct}%</td>
    </tr>
  );
}

function BasketballSummary({ data }) {
  const { game, final_score, top_performers, all_players } = data;
  const topAIds = top_performers.team_a.map(p => p.player_id);
  const topBIds = top_performers.team_b.map(p => p.player_id);
  return (
    <>
      <ScoreHeader
        eventName={game.name}
        date={game.date}
        teamAName={game.team_a.name}
        teamALogo={game.team_a.logo}
        teamBName={game.team_b.name}
        teamBLogo={game.team_b.logo}
        finalA={final_score.team_a}
        finalB={final_score.team_b}
        statusLabel={game.status === 'completed' ? 'Final' : null}
      />
      <div className="mb-8">
        <h2 className="text-lg font-bold text-[#00247D] mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
          <Trophy className="w-5 h-5 text-amber-500" /> TOP PERFORMERS
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { team: game.team_a.name, players: top_performers.team_a },
            { team: game.team_b.name, players: top_performers.team_b }
          ].map(({ team, players }) => (
            <Card key={team}>
              <CardContent className="p-4">
                <h4 className="text-xs font-bold text-slate-500 uppercase mb-3">{team}</h4>
                <div className="space-y-3">
                  {players.map((p, i) => (
                    <div key={p.player_id} className="flex items-center gap-3" data-testid={`top-perf-${p.player_id}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</div>
                      <span className="font-semibold text-sm text-slate-800 flex-1">#{p.jersey} {p.name}</span>
                      <div className="text-right text-xs text-slate-500">
                        <span className="font-bold text-[#00247D] text-sm mr-2">{p.pts}pts</span>
                        {p.reb}reb {p.ast}ast
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      {[
        { teamName: game.team_a.name, players: all_players.team_a, topIds: topAIds },
        { teamName: game.team_b.name, players: all_players.team_b, topIds: topBIds }
      ].map(({ teamName, players, topIds }) => (
        <Card key={teamName} className="mb-6 overflow-hidden">
          <div className="bg-[#00247D] px-4 py-3">
            <h3 className="text-white font-bold uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{teamName}</h3>
          </div>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center w-10">#</th>
                    <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase">Player</th>
                    <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">PTS</th>
                    <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">REB</th>
                    <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">AST</th>
                    <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">STL</th>
                    <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">BLK</th>
                    <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">TO</th>
                    <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">FG%</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map(p => <BasketballPlayerRow key={p.player_id} player={p} highlight={topIds.includes(p.player_id)} />)}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------//
// Sport-agnostic summary (soccer / volleyball / netball / pickleball)        //
// ---------------------------------------------------------------------------//

function statValue(player, key) {
  return (player.stats && player.stats[key]) || 0;
}

function SportPlayerRow({ player, statColumns, primaryKey, highlight }) {
  return (
    <tr className={`border-b border-slate-100 ${highlight ? 'bg-amber-50' : ''}`} data-testid={`sport-row-${player.player_id || player.name}`}>
      <td className="px-2 py-2 text-sm font-mono text-slate-500 text-center">{player.jersey_number}</td>
      <td className="px-2 py-2 text-sm font-semibold text-slate-800">
        {player.name}
        {highlight && <Star className="w-3 h-3 inline ml-1 text-amber-500" />}
      </td>
      {statColumns.map(col => {
        const v = statValue(player, col.key);
        const isPrimary = col.key === primaryKey;
        return (
          <td
            key={col.key}
            className={`px-2 py-2 text-sm text-center ${isPrimary ? 'font-bold text-[#00247D]' : 'text-slate-600'}`}
          >
            {v}
          </td>
        );
      })}
    </tr>
  );
}

function TopPerformerRow({ player, index, statColumns, primaryKey }) {
  // Show the primary stat prominently, then the next two non-primary
  // stats so soccer shows "2 goals · 1A · 3sh", volleyball shows
  // "12 kills · 5B · 4D", etc.
  const secondary = statColumns.filter(c => c.key !== primaryKey).slice(0, 2);
  const primaryLabel = statColumns.find(c => c.key === primaryKey);
  return (
    <div className="flex items-center gap-3" data-testid={`top-perf-${player.player_id || player.name}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
        {index + 1}
      </div>
      <span className="font-semibold text-sm text-slate-800 flex-1">
        {player.jersey_number ? `#${player.jersey_number} ` : ''}{player.name}
      </span>
      <div className="text-right text-xs text-slate-500">
        {primaryLabel && (
          <span className="font-bold text-[#00247D] text-sm mr-2">
            {statValue(player, primaryKey)} {primaryLabel.label}
          </span>
        )}
        {secondary.map((c, i) => (
          <span key={c.key} className="ml-1">
            {statValue(player, c.key)}{c.short ? ` ${c.short}` : c.label.slice(0, 1).toUpperCase()}
            {i < secondary.length - 1 ? ',' : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function SportSummary({ data }) {
  const {
    sport_label, stat_columns, primary_stat_key,
    game, final_score, sets, top_performers, all_players, has_box_score, cricket,
  } = data;

  const scoreA = cricket ? runsWickets(final_score?.team_a, cricket.team_a.wickets) || null : final_score?.team_a;
  const scoreB = cricket ? runsWickets(final_score?.team_b, cricket.team_b.wickets) || null : final_score?.team_b;
  const setSummary = sets && sets.length
    ? sets.map(s => `${s.a}-${s.b}`).join(', ')
    : null;

  return (
    <>
      <ScoreHeader
        eventName={game.name}
        date={game.date}
        teamAName={game.team_a.name}
        teamALogo={game.team_a.logo}
        teamBName={game.team_b.name}
        teamBLogo={game.team_b.logo}
        finalA={scoreA}
        finalB={scoreB}
        subA={cricket?.team_a.overs ? `(${cricket.team_a.overs} Overs)` : null}
        subB={cricket?.team_b.overs ? `(${cricket.team_b.overs} Overs)` : null}
        statusLabel={game.status === 'completed' ? 'Final' : null}
        sportBadge={sport_label}
        setSummary={setSummary}
        theme={game.share_theme}
      />

      {!has_box_score ? (
        <Card className="mb-6">
          <CardContent className="p-6 text-center text-slate-500">
            <ClipboardList className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">
              The final score is posted, but no per-player box score has been entered for this match yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Top Performers */}
          <div className="mb-8">
            <h2 className="text-lg font-bold text-[#00247D] mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Trophy className="w-5 h-5 text-amber-500" /> TOP PERFORMERS
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { team: game.team_a.name, players: top_performers.team_a },
                { team: game.team_b.name, players: top_performers.team_b }
              ].map(({ team, players }) => (
                <Card key={team}>
                  <CardContent className="p-4">
                    <h4 className="text-xs font-bold text-slate-500 uppercase mb-3">{team}</h4>
                    {players.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">No box score entered for this team.</p>
                    ) : (
                      <div className="space-y-3">
                        {players.map((p, i) => (
                          <TopPerformerRow
                            key={p.player_id || `${p.name}-${i}`}
                            player={p} index={i}
                            statColumns={stat_columns}
                            primaryKey={primary_stat_key}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Full Box Scores */}
          {[
            { teamName: game.team_a.name, players: all_players.team_a, topPlayers: top_performers.team_a },
            { teamName: game.team_b.name, players: all_players.team_b, topPlayers: top_performers.team_b }
          ].map(({ teamName, players, topPlayers }) => {
            const topIds = new Set(topPlayers.map(p => p.player_id));
            return (
              <Card key={teamName} className="mb-6 overflow-hidden">
                <div className="px-4 py-3 flex items-center justify-between" style={shareHero(game.share_theme).band} data-testid="sport-team-bar">
                  <h3 className="font-bold uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{teamName}</h3>
                  <span className="text-xs opacity-60 uppercase tracking-wider">
                    {players.length} player{players.length === 1 ? '' : 's'}
                  </span>
                </div>
                <CardContent className="p-0">
                  {players.length === 0 ? (
                    <p className="p-4 text-sm text-slate-500 italic">No box score entered for this team.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center w-10">#</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase">Player</th>
                            {stat_columns.map(col => (
                              <th key={col.key} title={col.label} className="px-2 py-2 text-[10px] font-bold text-slate-500 text-center">
                                {col.short || col.label.toUpperCase()}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {players.map((p, idx) => (
                            <SportPlayerRow
                              key={p.player_id || `${p.name}-${idx}`}
                              player={p}
                              statColumns={stat_columns}
                              primaryKey={primary_stat_key}
                              highlight={topIds.has(p.player_id)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
          <StatLegend sport={data.sport} codes={stat_columns.map((c) => c.short || c.label)} testId="match-stats-legend" />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------//
// Shared score header                                                        //
// ---------------------------------------------------------------------------//

function ScoreHeader({
  eventName, date,
  teamAName, teamALogo,
  teamBName, teamBLogo,
  finalA, finalB, subA, subB,
  statusLabel, sportBadge, setSummary, theme,
}) {
  const hero = shareHero(theme);
  return (
    <Card className="mb-8 overflow-hidden">
      <div className="p-6 relative" style={hero.band} data-testid="score-header">
        <p className="text-xs uppercase mb-4 text-center" data-testid="score-header-title">
          <span className="opacity-60">{eventName}{date ? ` · ${date}` : ''}</span>
          {sportBadge ? <span className="ml-2" style={{ color: hero.accent }}>{sportBadge}</span> : null}
        </p>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-shrink-0 w-16 sm:w-28 md:w-36 flex justify-center">
            {teamALogo && (
              <img
                src={`${API_URL}/api/files/${teamALogo}`}
                alt={`${teamAName} logo`}
                className="w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 object-contain drop-shadow-lg"
                data-testid="summary-team-a-logo"
              />
            )}
          </div>
          <div className="flex items-center justify-center gap-6 flex-1">
            <div className="text-right">
              <div className="text-xs sm:text-sm uppercase opacity-70 tracking-wider truncate max-w-[10rem] sm:max-w-[14rem]">{teamAName}</div>
              <div className="text-4xl sm:text-6xl font-black tabular-nums leading-none drop-shadow" data-testid="final-score-a">
                {finalA ?? '—'}
              </div>
              {subA && <div className="text-xs opacity-70 mt-1 tabular-nums" data-testid="final-overs-a">{subA}</div>}
            </div>
            <div className="text-xl sm:text-2xl opacity-30 font-bold">—</div>
            <div className="text-left">
              <div className="text-xs sm:text-sm uppercase opacity-70 tracking-wider truncate max-w-[10rem] sm:max-w-[14rem]">{teamBName}</div>
              <div className="text-4xl sm:text-6xl font-black tabular-nums leading-none drop-shadow" data-testid="final-score-b">
                {finalB ?? '—'}
              </div>
              {subB && <div className="text-xs opacity-70 mt-1 tabular-nums" data-testid="final-overs-b">{subB}</div>}
            </div>
          </div>
          <div className="flex-shrink-0 w-16 sm:w-28 md:w-36 flex justify-center">
            {teamBLogo && (
              <img
                src={`${API_URL}/api/files/${teamBLogo}`}
                alt={`${teamBName} logo`}
                className="w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 object-contain drop-shadow-lg"
                data-testid="summary-team-b-logo"
              />
            )}
          </div>
        </div>
        {setSummary && (
          <p className="text-xs mt-3 text-center tracking-wider font-mono" style={{ color: hero.accent }} data-testid="set-summary">
            {setSummary}
          </p>
        )}
        {statusLabel && (
          <p className="text-xs opacity-50 mt-3 uppercase text-center tracking-wider">{statusLabel}</p>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------//
// Route entry                                                                //
// ---------------------------------------------------------------------------//

export default function MatchStats() {
  const { matchId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get('event');
  const shareToken = searchParams.get('share');

  const [basketballData, setBasketballData] = useState(null);
  const [sportData, setSportData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sport AI recap state (basketball redirects out of this page, so
  // these only matter for the non-basketball path).
  const [recap, setRecap] = useState(null);
  const [recapLoading, setRecapLoading] = useState(false);

  // Logos: we pass raw URLs straight to the RecapCard. The share-capture
  // pipeline (`preloadImagesForCapture(node)` in `buildRecapImage`) walks
  // every <img> and inlines it as a base-64 data URL immediately before
  // `html-to-image` serializes the DOM. That eliminates the race the old
  // "preload-into-React-state via useEffect" approach suffered from on
  // iPhone users who tap the share button before the useEffect finishes.
  const [sharingRecap, setSharingRecap] = useState(false);
  const recapCardRef = useRef(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        // 1. Try the basketball live-tracker linkage first — if a
        //    basketball stats game is linked to this match, /stats/summary
        //    is the richer view so we redirect there.
        const linkRes = await fetch(`${API_URL}/api/stats/games/by-match/${matchId}`);
        if (linkRes.ok) {
          const { game } = await linkRes.json();
          if (game && game.id) {
            const target = `/stats/summary/${game.id}${shareToken ? `?share=${shareToken}` : ''}`;
            navigate(target, { replace: true });
            return;
          }
        }
        // 2. Otherwise, if we know the event, fetch the sport-agnostic
        //    summary and render it below.
        if (eventId) {
          const headers = {};
          const token = localStorage.getItem('session_token');
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const res = await fetch(
            `${API_URL}/api/events/${eventId}/matches/${matchId}/summary`,
            { headers },
          );
          if (res.ok) setSportData(await res.json());
          // Pull any cached recap so returning fans see it immediately
          // instead of an empty "Generate" button.
          const rcp = await fetch(
            `${API_URL}/api/events/${eventId}/matches/${matchId}/ai-recap`,
            { headers },
          );
          if (rcp.ok) {
            const rj = await rcp.json();
            if (rj?.recap) setRecap(rj.recap);
          }
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [matchId, eventId, shareToken, navigate]);

  const generateRecap = async (force = false) => {
    if (!eventId) return;
    setRecapLoading(true);
    try {
      const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || '';
      const params = new URLSearchParams();
      if (tz) params.append('tz', tz);
      if (force) params.append('regenerate', 'true');
      const qs = params.toString();
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('session_token');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(
        `${API_URL}/api/events/${eventId}/matches/${matchId}/ai-recap${qs ? `?${qs}` : ''}`,
        { method: 'POST', headers },
      );
      if (res.ok) {
        const data = await res.json();
        setRecap(data.recap);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not generate recap');
      }
    } catch { toast.error('Network error while generating recap'); }
    finally { setRecapLoading(false); }
  };

  const buildRecapImage = async () => {
    if (!recapCardRef.current) return null;
    const node = recapCardRef.current;
    const restoreImages = await preloadImagesForCapture(node);
    try {
      return await toPng(node, {
        backgroundColor: recapPalette(sportData?.game?.share_theme).base,
        cacheBust: true,
        pixelRatio: 2,
        width: 1080,
        height: 1080,
        canvasWidth: 1080,
        canvasHeight: 1080,
      });
    } finally { restoreImages(); }
  };

  const handleShareRecap = async () => {
    if (sharingRecap || !sportData) return;
    setSharingRecap(true);
    try {
      const dataUrl = await buildRecapImage();
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const g = sportData.game;
      const fileName = `${g.team_a.name}-vs-${g.team_b.name}-recap.png`
        .replace(/\s+/g, '-').replace(/[^a-zA-Z0-9.-]/g, '');
      const file = new File([blob], fileName, { type: 'image/png' });
      const title = `${g.team_a.name} ${sportData.final_score?.team_a ?? ''} - ${g.team_b.name} ${sportData.final_score?.team_b ?? ''}`;
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        try {
          await navigator.share({ files: [file], title });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      }
      // Fallback: download
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = fileName;
      a.click();
      toast.success('Recap saved — share it from your photos');
    } catch (err) {
      console.error('share recap failed', err);
      toast.error('Couldn’t generate recap image');
    } finally {
      setSharingRecap(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" data-testid="match-stats-loading" />
    </div>
  );

  const data = basketballData || sportData;
  if (!data) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center text-center px-4">
      <div data-testid="match-stats-empty">
        <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-bold text-slate-700 mb-2">No Stats Available</h2>
        <p className="text-slate-500 text-sm mb-4">
          Stats have not been entered for this game yet.
        </p>
        <Link to="/" className="text-[#00247D] text-sm hover:underline">Back to Schedule Guru</Link>
      </div>
    </div>
  );

  // sportData carries an event share_token when the event is public
  const backHref = sportData?.game?.event_id
    ? (shareToken
        ? `/share/${shareToken}`
        : (sportData.game.share_token ? `/share/${sportData.game.share_token}` : `/schedule/${sportData.game.event_id}`))
    : null;

  // Prepare Share Recap card props from sportData (basketball redirects
  // away from this route, so we only render the card for sports).
  let recapProps = null;
  if (sportData?.has_box_score) {
    const g = sportData.game;
    const fs = sportData.final_score || {};
    const sa = fs.team_a;
    const sb = fs.team_b;
    let winnerKey = null;
    if (typeof sa === 'number' && typeof sb === 'number') {
      if (sa > sb) winnerKey = 'a';
      else if (sb > sa) winnerKey = 'b';
    }
    const winnerName =
      winnerKey === 'a' ? g.team_a.name :
      winnerKey === 'b' ? g.team_b.name : null;
    const winnerMargin = winnerKey ? Math.abs(sa - sb) : 0;
    const primary = sportData.primary_stat_key;
    const primaryLabel = (sportData.stat_columns || []).find(c => c.key === primary);
    const secondaries = (sportData.stat_columns || []).filter(c => c.key !== primary).slice(0, 2);
    const asPerformer = (p) => {
      const stats = p.stats || {};
      const primaryVal = Number(stats[primary] || 0);
      const primaryTxt = primaryLabel
        ? `${primaryVal} ${primaryLabel.label.split(' ')[0]}`
        : String(primaryVal);
      const secondary = secondaries
        .map(c => `${Number(stats[c.key] || 0)}${c.short ? ` ${c.short}` : c.label.slice(0, 1).toUpperCase()}`)
        .join(' ');
      return {
        id: p.player_id || `${p.name}-${p.jersey_number}`,
        jersey: p.jersey_number,
        name: p.name,
        primary: primaryTxt,
        secondary,
      };
    };
    const setSummary = (sportData.sets && sportData.sets.length)
      ? sportData.sets.map(s => `${s.a}-${s.b}`).join(', ')
      : null;
    const dateLabel = formatGameDate(g.date);
    const ck = sportData.cricket;
    const oversLine = ck && (ck.team_a.overs || ck.team_b.overs)
      ? `${g.team_a.name} ${ck.team_a.overs || '–'} Overs · ${g.team_b.name} ${ck.team_b.overs || '–'} Overs`
      : null;
    recapProps = {
      teamA: { name: g.team_a.name },
      teamB: { name: g.team_b.name },
      scoreA: ck ? runsWickets(sa, ck.team_a.wickets) : sa,
      scoreB: ck ? runsWickets(sb, ck.team_b.wickets) : sb,
      winnerKey,
      winnerLabel: winnerName ? (ck ? `${winnerName} win` : `${winnerName} win by ${winnerMargin}`) : null,
      headerLabel: g.event_name || g.event_organization || g.name,
      dateLabel,
      sportBadge: sportData.sport_label,
      subheading: `${g.team_a.name} vs ${g.team_b.name}`,
      setSummary: setSummary || oversLine,
      teamAPerformers: (sportData.top_performers?.team_a || []).map(asPerformer),
      teamBPerformers: (sportData.top_performers?.team_b || []).map(asPerformer),
      eventLogoUrl: (g.event_logo || g.event_organization_logo)
        ? `${API_URL}/api/files/${g.event_logo || g.event_organization_logo}`
        : '',
      teamALogoUrl: g.team_a?.logo ? `${API_URL}/api/files/${g.team_a.logo}` : '',
      teamBLogoUrl: g.team_b?.logo ? `${API_URL}/api/files/${g.team_b.logo}` : '',
      theme: g.share_theme,
    };
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="match-stats-page">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 px-2"
              onClick={() => (backHref ? navigate(backHref) : window.history.back())}
              data-testid="match-stats-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-base sm:text-xl font-bold text-[#00247D] truncate" style={{ fontFamily: 'Barlow Condensed' }}>
              GAME SUMMARY
            </h1>
          </div>
          {recapProps && (
            <Button
              size="sm"
              onClick={handleShareRecap}
              disabled={sharingRecap}
              className="font-bold hover:brightness-110 disabled:opacity-60 px-2 sm:px-3"
              style={shareHero(sportData?.game?.share_theme).accentFill}
              data-testid="sport-share-recap-btn"
            >
              {sharingRecap
                ? <><Loader2 className="w-4 h-4 sm:mr-1 animate-spin" /> <span className="hidden sm:inline">Generating</span></>
                : <><Share2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Share Recap</span></>}
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {sportData ? <SportSummary data={sportData} /> : <BasketballSummary data={basketballData} />}

        {/* AI Sport Recap — only shown on the sport summary path */}
        {sportData?.has_box_score && (
          <Card className="mt-6" data-testid="sport-ai-recap-section">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-[#00247D] uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                  AI Game Recap
                </h3>
                <div className="flex items-center gap-2">
                  {!recap && (
                    <Button
                      size="sm"
                      onClick={() => generateRecap(false)}
                      disabled={recapLoading}
                      className="bg-[#00247D] hover:bg-[#001A5C]"
                      data-testid="sport-generate-recap-btn"
                    >
                      {recapLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                      {recapLoading ? 'Generating...' : 'Generate Recap'}
                    </Button>
                  )}
                  {recap && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => generateRecap(true)}
                      disabled={recapLoading}
                      className="border-[#00247D] text-[#00247D] hover:bg-blue-50"
                      data-testid="sport-regenerate-recap-btn"
                      title="Delete the cached recap and generate a fresh one from the current box score"
                    >
                      {recapLoading
                        ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Regenerating...</>
                        : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Regenerate</>}
                    </Button>
                  )}
                </div>
              </div>
              {recap ? (
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="sport-recap-text">{recap}</p>
              ) : (
                <p className="text-sm text-slate-400">Click "Generate Recap" for an AI-written game summary grounded on the box score above.</p>
              )}
            </CardContent>
          </Card>
        )}

        <div className="text-center pt-4 text-xs text-slate-400">
          <Link to="/" className="hover:text-[#00247D]">
            Powered by <span className="font-semibold">scheduleguru.ws</span>
          </Link>
        </div>
      </div>

      {/* Off-screen Recap Card mount — captured by html-to-image on
          Share Recap tap. Only rendered when we have box-score data. */}
      {recapProps && (
        <div aria-hidden="true" style={{ position: 'fixed', left: '-99999px', top: 0, pointerEvents: 'none' }}>
          <RecapSquareCard innerRef={recapCardRef} {...recapProps} />
        </div>
      )}
    </div>
  );
}
